import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Grid, Paper, Typography, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, Alert, CircularProgress, Chip, useMediaQuery, useTheme,
  Checkbox, Stack, IconButton
} from '@mui/material';
import { PointOfSale, LockOpen, Lock, Notes } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  API_BASE_URL, TURNO_FORMDATA, TURNO_ABRIR, TURNO_CERRAR, TURNO_GET,
  CHECKLIST_TURNO, CHECKLIST_MARCAR
} from '../services/apiConfig';
import TituloPagina from './utils/TituloPagina.jsx';
import VolverBreadcrumb from './utils/VolverBreadcrumb.jsx';
import Cargando from './utils/Cargando.jsx';
import TildeExito from './utils/TildeExito.jsx';
import { reproducirSonidoExito } from '../services/sonido';
import { avisarExito, avisarError } from '../services/toast';
import axios from 'axios';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const diaSemanaDe = (f) => {
  if (!f) return '';
  const d = new Date(String(f).replace(' ', 'T'));
  return isNaN(d) ? '' : DIAS_SEMANA[d.getDay()];
};

const fmtMoneda = (n) => n == null ? '—' : `$${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtFecha = (f) => {
  if (!f) return '—';
  const d = new Date(String(f).replace(' ', 'T'));
  if (isNaN(d)) return String(f);
  return `${d.toLocaleDateString('es-AR')} ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

function Turno() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [params] = useSearchParams();
  const id = params.get('id');
  const sucursalIdParam = params.get('sucursal_id');
  const perfil = Number(JSON.parse(sessionStorage.getItem('userData') || '{}')?.perfil);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [cerrando, setCerrando] = useState(false);
  const [mostrarExitoCierre, setMostrarExitoCierre] = useState(false);

  const [sucursales, setSucursales] = useState([]);
  const [sucursalSel, setSucursalSel] = useState('');
  const [turnoAbierto, setTurnoAbierto] = useState(null); // turno_abierto de la sucursal elegida (sin id=)
  const [turnoDetalle, setTurnoDetalle] = useState(null); // detalle completo cuando entramos por ?id=

  // Apertura
  const [obsApertura, setObsApertura] = useState('');

  // Cierre
  const [obsCierre, setObsCierre] = useState('');

  // Checklist del turno (solo informativo, no bloquea el cierre)
  const [checklist, setChecklist] = useState([]);
  const [checklistCargado, setChecklistCargado] = useState(false);
  const [obsAbiertas, setObsAbiertas] = useState({});

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (id) {
        const res = await axios.get(API_BASE_URL + TURNO_GET, { params: { id }, headers: authHeader() });
        setTurnoDetalle(res.data?.data ?? null);
      } else {
        const fd = await axios.get(API_BASE_URL + TURNO_FORMDATA, { headers: authHeader() });
        const sucs = fd.data?.data?.sucursales ?? [];
        setSucursales(sucs);
        // Con una sola sucursal no hay ambigüedad posible: se preselecciona.
        // Con varias, nunca se elige una "por default" — quedaría fácil abrir
        // o cerrar el turno de la sucursal equivocada sin darse cuenta.
        const preSel = sucursalIdParam && sucs.some((s) => Number(s.id) === Number(sucursalIdParam))
          ? Number(sucursalIdParam)
          : (sucs.length === 1 ? Number(sucs[0].id) : '');
        setSucursalSel(preSel);
        const s = sucs.find((x) => Number(x.id) === Number(preSel));
        setTurnoAbierto(s?.turno_abierto ?? null);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Error al cargar datos.');
    } finally {
      setLoading(false);
    }
  }, [id, sucursalIdParam]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!id || !turnoDetalle || turnoDetalle.estado !== 'abierto') return;
    axios.get(API_BASE_URL + CHECKLIST_TURNO, { params: { turno_id: id }, headers: authHeader() })
      .then((res) => setChecklist(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => {})
      .finally(() => setChecklistCargado(true));
  }, [id, turnoDetalle]);

  const marcarTarea = (tareaId, completado, observaciones) => {
    axios.post(API_BASE_URL + CHECKLIST_MARCAR, {
      turno_id: id, tarea_id: tareaId, completado: completado ? 1 : 0, observaciones,
    }, { headers: authHeader() })
      .then((res) => { if (!res.data?.success) throw new Error(res.data?.message); })
      .catch((e) => avisarError(e.response?.data?.message || e.message || 'Error al guardar la tarea.'));
  };

  const onToggleTarea = (c) => {
    const completado = !c.completado;
    setChecklist((prev) => prev.map((x) => x.tarea_id === c.tarea_id ? { ...x, completado } : x));
    marcarTarea(c.tarea_id, completado, c.observaciones || '');
  };

  const onChangeObs = (tareaId, value) => {
    setChecklist((prev) => prev.map((x) => x.tarea_id === tareaId ? { ...x, observaciones: value } : x));
  };

  const onBlurObs = (c) => marcarTarea(c.tarea_id, c.completado, c.observaciones || '');

  const onCambiarSucursal = (sid) => {
    setSucursalSel(sid);
    const s = sucursales.find((x) => Number(x.id) === Number(sid));
    setTurnoAbierto(s?.turno_abierto ?? null);
  };

  const abrirTurno = async () => {
    setError('');
    if (!sucursalSel) { setError('Elegí una sucursal.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(API_BASE_URL + TURNO_ABRIR, {
        sucursal_id: sucursalSel, observaciones: obsApertura,
      }, { headers: authHeader() });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al abrir el turno.');
      avisarExito('Turno abierto correctamente.');
      setTimeout(() => navigate('/turnos'), 900);
    } catch (e) {
      avisarError(e.response?.data?.message || e.message || 'Error al abrir el turno.');
      setSubmitting(false);
    }
  };

  const cerrarTurno = async (turnoId) => {
    setError('');
    setSubmitting(true);
    setCerrando(true);
    try {
      const res = await axios.post(API_BASE_URL + TURNO_CERRAR, {
        id: turnoId, observaciones: obsCierre,
      }, { headers: authHeader() });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al cerrar el turno.');
      setMostrarExitoCierre(true);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Error al cerrar el turno.');
      setSubmitting(false);
      setCerrando(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
        <MenuInicial /><Cargando />
      </Container>
    );
  }

  // --- Vista de detalle (entramos por ?id=) ---
  if (id) {
    const t = turnoDetalle;
    const abierto = t && t.estado === 'abierto';
    return (
      <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
        <TildeExito
          submitting={cerrando}
          mostrar={mostrarExitoCierre}
          onFinalizado={() => navigate('/turnos')}
          sonido={reproducirSonidoExito}
          mensaje="Turno cerrado"
        />
        <MenuInicial />
        <Box sx={{ my: 3 }}>
          <TituloPagina icon={PointOfSale} titulo={`Turno #${id}`} subtitulo={t?.sucursal} ayuda="Si el turno sigue abierto, podés cerrarlo desde acá." />
          <VolverBreadcrumb to="/turnos" label="Turnos de caja" />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {t && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Chip size="small" icon={abierto ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
                  label={abierto ? 'Abierto' : 'Cerrado'} color={abierto ? 'success' : 'default'} />
                {t.sucursal && <Chip size="small" variant="outlined" label={t.sucursal} />}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary" display="block">Sucursal</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{t.sucursal || '—'}</Typography></Grid>
                <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary" display="block">Apertura</Typography><Typography variant="body2">{fmtFecha(t.fecha_apertura)} ({t.usuario_apertura})</Typography></Grid>
                <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary" display="block">Cierre</Typography><Typography variant="body2">{abierto ? '—' : `${fmtFecha(t.fecha_cierre)} (${t.usuario_cierre})`}</Typography></Grid>
                {perfil !== 3 && (
                  <Grid item xs={6} md={4}><Typography variant="caption" color="text.secondary" display="block">Registrado</Typography><Typography variant="body2">{fmtMoneda(t.ventas_total ?? t.total_ventas)} ({t.ventas_cantidad ?? 0})</Typography></Grid>
                )}
              </Grid>
            </Paper>
          )}
          {abierto && checklistCargado && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Checklist del turno</Typography>
              {checklist.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay tareas de checklist asignadas para {diaSemanaDe(t.fecha_apertura)}, Turno {t.numero_turno_dia} en esta sucursal.
                  Se configuran en "Checklist de tareas".
                </Typography>
              ) : (
              <Stack spacing={1}>
                {checklist.map((c) => {
                  const abierta = obsAbiertas[c.tarea_id] ?? !!c.observaciones;
                  return (
                    <Box key={c.tarea_id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Checkbox checked={!!c.completado} onChange={() => onToggleTarea(c)} />
                        <Typography variant="body2" sx={{ flex: 1 }}>{c.descripcion}</Typography>
                        <IconButton size="small" onClick={() => setObsAbiertas((p) => ({ ...p, [c.tarea_id]: !abierta }))} title="Observaciones">
                          <Notes fontSize="small" color={c.observaciones ? 'primary' : 'action'} />
                        </IconButton>
                      </Box>
                      {abierta && (
                        <TextField
                          fullWidth size="small" placeholder="Observaciones (opcional)"
                          sx={{ ml: { xs: 0, sm: 5 }, mt: 0.5, maxWidth: 480 }}
                          value={c.observaciones || ''}
                          onChange={(e) => onChangeObs(c.tarea_id, e.target.value)}
                          onBlur={() => onBlurObs(c)}
                        />
                      )}
                    </Box>
                  );
                })}
              </Stack>
              )}
            </Paper>
          )}
          {abierto && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Cerrar turno</Typography>
              {(() => {
                const pendientes = checklist.filter((c) => !c.completado);
                return pendientes.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Quedan {pendientes.length} tarea{pendientes.length === 1 ? '' : 's'} del checklist sin marcar: {pendientes.map((p) => p.descripcion).join(', ')}.
                    Podés cerrar el turno igual — quedan marcadas como pendientes en el mail de resumen.
                  </Alert>
                );
              })()}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Observaciones" value={obsCierre} onChange={(e) => setObsCierre(e.target.value)} />
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="contained" color="primary" onClick={() => cerrarTurno(t.id)} disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Lock />}>
                  {submitting ? 'Cerrando…' : 'Cerrar turno y enviar resumen'}
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      </Container>
    );
  }

  // --- Vista de apertura/cierre por sucursal (sin ?id=) ---
  const sucursalNombre = sucursales.find((s) => Number(s.id) === Number(sucursalSel))?.nombre;

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={PointOfSale}
          titulo="Turno de caja"
          subtitulo={turnoAbierto ? 'Cerrar turno' : 'Abrir turno'}
          ayuda={turnoAbierto
            ? 'Al cerrar el turno se manda un mail de resumen con lo registrado a la sucursal.'
            : 'No vas a poder registrar ventas en esta sucursal hasta abrir el turno.'}
        />
        <VolverBreadcrumb to="/turnos" label="Turnos de caja" />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {sucursales.length === 0 ? (
          <Alert severity="warning">No tenés sucursales asignadas.</Alert>
        ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={sucursales.length <= 1}>
              <InputLabel>Sucursal</InputLabel>
              <Select label="Sucursal" value={sucursalSel} onChange={(e) => onCambiarSucursal(e.target.value)} displayEmpty={sucursales.length > 1}>
                {sucursales.length > 1 && <MenuItem value=""><em>Elegí una sucursal…</em></MenuItem>}
                {sucursales.map((s) => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
              </Select>
            </FormControl>

            {!sucursalSel ? (
              <Alert severity="info">Elegí de qué sucursal querés ver o abrir el turno.</Alert>
            ) : turnoAbierto ? (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Hay un turno abierto en {sucursalNombre} desde {fmtFecha(turnoAbierto.fecha_apertura)}.
                </Alert>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" color="primary" onClick={() => navigate(`/turno?id=${turnoAbierto.id}`)}>
                    Ver turno y cerrar
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Observaciones" value={obsApertura} onChange={(e) => setObsApertura(e.target.value)} />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button variant="contained" color="primary" onClick={abrirTurno} disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <LockOpen />}>
                    {submitting ? 'Abriendo…' : 'Abrir turno'}
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        )}
      </Box>
    </Container>
  );
}

export default Turno;
